import React from 'react';
import { FiX } from 'react-icons/fi';
import useMonitoringDivergences from './hooks/useMonitoringDivergences';
import APaginationInline from '../../molecules/APaginationInline';
import { Classification, DivergenceRecord } from '../../../services/monitoringApi';

interface MonitoringDivergencesDrawerProps {
    isOpen: boolean;
    item: Classification;
    onClose: () => void;
}

const formatDateTime = (dateStr: string) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString('pt-BR') + ' ' + d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
};

const formatValue = (value: unknown): string => {
    if (value === null || value === undefined) return '(vazio)';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
};

const DivergenceCard: React.FC<{ record: DivergenceRecord }> = ({ record }) => {
    return (
        <div className="mb-4">
            <div className="flex items-center gap-2 mb-2">
                <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                    {formatDateTime(record.created_at)}
                </span>
            </div>

            <div className="border border-gray-200 dark:border-slate-600 rounded-md overflow-hidden">
                <table className="min-w-full text-xs">
                    <thead className="bg-gray-50 dark:bg-slate-700">
                        <tr>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                                Campo
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                                Anterior
                            </th>
                            <th className="px-3 py-2 text-left font-medium text-gray-600 dark:text-gray-300">
                                Atual
                            </th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-slate-600">
                        {record.divergences.map((div, idx) => (
                            <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-slate-700">
                                <td className="px-3 py-2 text-gray-800 dark:text-gray-200 font-medium">
                                    {div.field}
                                </td>
                                <td className="px-3 py-2 text-red-600 dark:text-red-400">
                                    {formatValue(div.previous_value)}
                                </td>
                                <td className="px-3 py-2 text-green-600 dark:text-green-400">
                                    {formatValue(div.current_value)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const MonitoringDivergencesDrawer: React.FC<MonitoringDivergencesDrawerProps> = ({ isOpen, item, onClose }) => {
    const {
        items, total, page, setPage, totalPages, isLoading,
        dateFrom, setDateFrom, dateTo, setDateTo,
    } = useMonitoringDivergences(isOpen ? item.person_id : null);

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-40 flex justify-end">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black bg-opacity-30" onClick={onClose} />

            {/* Drawer */}
            <div className="relative w-full max-w-lg bg-white dark:bg-slate-800 shadow-xl flex flex-col h-full z-50">
                {/* Header */}
                <div className="px-5 py-4 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                            Divergências
                        </h2>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                            {item.person_id}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-1.5 rounded hover:bg-gray-200 dark:hover:bg-slate-600"
                        aria-label="Fechar"
                    >
                        <FiX className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                    </button>
                </div>

                {/* Filters */}
                <div className="px-5 py-3 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
                    <div className="flex gap-2">
                        <div className="flex-1">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">De</label>
                            <input
                                type="date"
                                value={dateFrom}
                                onChange={(e) => setDateFrom(e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>
                        <div className="flex-1">
                            <label className="block text-xs text-gray-500 dark:text-gray-400 mb-1">Até</label>
                            <input
                                type="date"
                                value={dateTo}
                                onChange={(e) => setDateTo(e.target.value)}
                                className="w-full px-2 py-1.5 border border-gray-300 dark:border-slate-600 rounded text-xs bg-white dark:bg-slate-700 text-gray-900 dark:text-gray-100"
                            />
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto px-5 py-4">
                    {isLoading ? (
                        <div className="flex justify-center py-8">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                        </div>
                    ) : items.length === 0 ? (
                        <p className="text-center text-sm text-gray-500 dark:text-gray-400 py-8">
                            Nenhuma divergência encontrada
                        </p>
                    ) : (
                        items.map((record) => (
                            <DivergenceCard key={record.id} record={record} />
                        ))
                    )}
                </div>

                {/* Footer pagination */}
                {totalPages > 1 && (
                    <div className="px-5 py-3 border-t border-gray-200 dark:border-slate-700 flex items-center justify-between flex-shrink-0">
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                            {total} registro{total !== 1 ? 's' : ''}
                        </span>
                        <APaginationInline
                            currentPage={page}
                            totalPages={totalPages}
                            onPageChange={setPage}
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default MonitoringDivergencesDrawer;
